import { Check } from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Dimensions,
  Keyboard,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text as RNText,
  View,
} from 'react-native';

import { Input } from '@/components/Input';
import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';

/**
 * Input + filtered dropdown over a fixed list of strings. The sport picker,
 * the school picker and the old inline school autocomplete were three copies
 * of this markup; it lives here once now.
 *
 * The dropdown overlays the fields below rather than pushing them down, and
 * flips above the input when the keyboard leaves no room beneath it. Both of
 * these screens run past the fold, so in-flow-and-always-below meant the list
 * opened behind the keyboard and you had to know to scroll.
 */
export function PickerField({
  label,
  icon,
  options,
  pinned,
  value,
  onSelect,
  placeholder,
  emptyLabel,
}: {
  label: string;
  icon?: LucideIcon;
  options: readonly string[];
  /** Escape-hatch option kept at the bottom of the list and never filtered
   *  out — without it, typing something off-list leaves an empty dropdown
   *  and no way forward. */
  pinned?: string;
  value: string | null;
  onSelect: (option: string | null) => void;
  placeholder: string;
  emptyLabel: string;
}) {
  const [text, setText] = useState(value ?? '');
  const [focused, setFocused] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const anchor = useRef<View>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // What we last told the parent. Lets the effect below tell a parent-driven
  // value change (prefill arriving, a form reset) apart from the echo of our
  // own onSelect — resyncing on the echo would wipe what the user is typing.
  const lastEmitted = useRef<string | null>(value);

  function emit(next: string | null) {
    lastEmitted.current = next;
    onSelect(next);
  }

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setText(value ?? '');
  }, [value]);

  // Re-measured on every open (and again when the keyboard finishes
  // animating, since its height isn't known until then).
  // Native only. Flipping exists to escape the on-screen keyboard, which the
  // web has no way to measure: react-native-web's Keyboard is a no-op stub,
  // and visualViewport shrinks for Safari's own chrome as much as for the
  // keyboard, so every heuristic built on it flipped the list constantly —
  // jumping it over the form while the page below sat empty and perfectly
  // scrollable. On web the list always opens downward, like every other web
  // dropdown, and the page scrolls if the keyboard is in the way.
  useEffect(() => {
    if (!focused || Platform.OS === 'web') return;

    const measure = () =>
      anchor.current?.measureInWindow((_x, y, _width, height) => {
        const keyboard = Keyboard.metrics()?.height ?? 0;
        const below = Dimensions.get('window').height - keyboard - (y + height);
        setFlipUp(below < DROPDOWN_MAX_HEIGHT);
      });

    measure();
    const shown = Keyboard.addListener('keyboardDidShow', measure);
    const hidden = Keyboard.addListener('keyboardDidHide', measure);

    return () => {
      shown.remove();
      hidden.remove();
    };
  }, [focused]);

  useEffect(() => () => clearBlurTimer(), []);

  function clearBlurTimer() {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    blurTimer.current = null;
  }

  const query = text.trim().toLowerCase();
  const matches = query ? options.filter((o) => o.toLowerCase().includes(query)) : options;
  const filtered = pinned && !matches.includes(pinned) ? [...matches, pinned] : matches;
  const showResults = focused && value !== text;

  /**
   * Typing has to move the selection, not just the label. Without this the
   * field can display one school and submit another: the user edits the text,
   * taps the submit button instead of a dropdown row, and the stale `value`
   * ships. Both pickers this replaced reconciled on change; failing safe
   * (clearing, which disables the button) beats failing silently wrong.
   */
  function handleChange(next: string) {
    setText(next);

    // Matched against `options`/`pinned` directly, NOT the `filtered` memo —
    // that is derived from the previous render's `text`, so backspacing out of
    // a no-match state ("Alabamaa" -> "Alabama") searched a list that had been
    // narrowed to nothing and silently failed to re-select. The field then
    // showed a real school while `value` stayed null, leaving the submit
    // button dead with nothing explaining why.
    const needle = next.trim().toLowerCase();
    const exact =
      options.find((o) => o.toLowerCase() === needle) ??
      (pinned && pinned.toLowerCase() === needle ? pinned : null);
    if (exact !== value) emit(exact);
  }

  function pick(option: string) {
    emit(option);
    setText(option);
    clearBlurTimer();
    setFocused(false);
    Keyboard.dismiss();
  }

  return (
    <View ref={anchor} style={[styles.anchor, showResults && styles.anchorOpen]}>
      <Input
        label={label}
        icon={icon}
        focused={focused}
        value={text}
        onChangeText={handleChange}
        onFocus={() => {
          clearBlurTimer();
          setFocused(true);
        }}
        // Delayed so a tap on a row lands before the dropdown unmounts. Held
        // in a ref so a quick blur→refocus (tapping the next field and coming
        // straight back) can't have the stale timer close the reopened list.
        onBlur={() => {
          clearBlurTimer();
          blurTimer.current = setTimeout(() => setFocused(false), 150);
        }}
        placeholder={placeholder}
        autoCapitalize="words"
        autoCorrect={false}
        autoComplete="off"
        spellCheck={false}
        returnKeyType="done"
      />
      {showResults && (
        <View style={[styles.dropdown, flipUp ? styles.dropdownAbove : styles.dropdownBelow]}>
          {filtered.length === 0 ? (
            <RNText style={styles.dropdownEmpty}>{emptyLabel}</RNText>
          ) : (
            <ScrollView keyboardShouldPersistTaps="handled" nestedScrollEnabled>
              {filtered.map((option) => {
                const selected = value === option;
                return (
                  <Pressable
                    key={option}
                    style={({ pressed }) => [styles.dropdownRow, pressed && styles.dropdownRowPressed]}
                    onPress={() => pick(option)}
                    accessibilityRole="button"
                    accessibilityLabel={`Select ${option}`}
                    accessibilityState={{ selected }}
                  >
                    <RNText style={styles.dropdownName}>{option}</RNText>
                    {selected && <Check color={colors.primary} size={18} />}
                  </Pressable>
                );
              })}
            </ScrollView>
          )}
        </View>
      )}
    </View>
  );
}

const DROPDOWN_MAX_HEIGHT = 280;

const styles = StyleSheet.create({
  anchor: { position: 'relative', zIndex: 1 },
  // The OPEN picker has to outrank its siblings, not just tie with them: two
  // pickers stacked (school above sport on the campaign form) paint in source
  // order, so an equal zIndex let the field below cover the open dropdown.
  // Callers must not wrap this in another View — on iOS zIndex only reorders
  // within one parent, so a wrapper would trap it below the next sibling.
  anchorOpen: { zIndex: 20 },
  dropdown: {
    position: 'absolute',
    left: 0,
    right: 0,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radii.lg,
    overflow: 'hidden',
    maxHeight: DROPDOWN_MAX_HEIGHT,
    ...shadows.card,
  },
  dropdownBelow: { top: '100%', marginTop: spacing.xs },
  dropdownAbove: { bottom: '100%', marginBottom: spacing.xs },
  dropdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  dropdownRowPressed: {
    backgroundColor: colors.background,
  },
  dropdownName: { fontFamily: fonts.bodyMedium, fontSize: fontSizes.base, color: colors.text },
  dropdownEmpty: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.textMuted,
    padding: spacing.base,
  },
});
